import clsx from 'clsx';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { PhoneInput as IntlPhoneInput, type PhoneInputRefType } from 'react-international-phone';

import {
  INVALID_CONTACT_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  INVALID_PHONE_MESSAGE,
} from './contactMessages';
import s from './inputs.module.scss';
import { normalizePhoneValue } from './phoneValue';

import 'react-international-phone/style.css';

type ContactMode = 'email' | 'phone';

const looksLikePhone = (value?: string): boolean => {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('+')) return true;
  return /^[\d\s()+.-]+$/.test(trimmed) && /\d/.test(trimmed);
};

export const ContactInput = ({
  label,
  value = '',
  onChange,
  onBlur,
  error,
  mandatory,
  className,
  name,
  autoComplete,
  emailPlaceholder = 'Email Address',
  phonePlaceholder = 'Mobile Number',
  defaultCountry = 'us',
}: {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  mandatory?: boolean;
  className?: string;
  name?: string;
  autoComplete?: string;
  emailPlaceholder?: string;
  phonePlaceholder?: string;
  defaultCountry?: string;
}) => {
  const [mode, setMode] = useState<ContactMode>(() => (looksLikePhone(value) ? 'phone' : 'email'));
  const [suppressError, setSuppressError] = useState(false);
  const phoneRef = useRef<PhoneInputRefType | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const didMount = useRef(false);
  const inputId = useId();

  useLayoutEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (mode === 'phone') phoneRef.current?.focus();
    else emailRef.current?.focus();
  }, [mode]);

  const switchTo = (next: ContactMode) => {
    if (next === mode) return;
    setMode(next);
    setSuppressError(true);
    onChange('');
  };

  const resolvedError =
    error === INVALID_CONTACT_MESSAGE
      ? mode === 'phone'
        ? INVALID_PHONE_MESSAGE
        : INVALID_EMAIL_MESSAGE
      : error;
  const shownError = suppressError ? undefined : resolvedError;

  return (
    <div className={clsx(s.InputRoot, className)}>
      <div className={s.ContactHeader}>
        <div className={s.Label}>
          {label && <span>{label}</span>}
          {mandatory && <span>*</span>}
        </div>
        <div className={s.ContactSwitch} role="tablist" aria-label="Choose Email or Mobile">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'email'}
            className={clsx(s.ContactSwitchOption, mode === 'email' && s.active)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => switchTo('email')}
          >
            Email
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'phone'}
            className={clsx(s.ContactSwitchOption, mode === 'phone' && s.active)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => switchTo('phone')}
          >
            Mobile
          </button>
        </div>
      </div>

      {/* The whole box focuses the field, not just the input's own line. `htmlFor` rather
          than nesting: the phone field puts its country button first, and an implicit
          label would forward clicks to that. */}
      <label htmlFor={inputId} className={clsx(s.InputWrapper, shownError && s.error)}>
        {mode === 'phone' ? (
          <IntlPhoneInput
            defaultCountry={defaultCountry}
            value={value}
            onChange={(phone, meta) => {
              // The input reports its dial-code prefill (e.g. `+1`) on mount, which
              // normalizes to '' - so switching to phone never surfaces an error or
              // writes a bare `+1` until the user types a real national number.
              const normalized = normalizePhoneValue(phone, meta.country.dialCode);
              if (normalized) setSuppressError(false);
              onChange(normalized);
            }}
            className="body-medium"
            placeholder={phonePlaceholder}
            inputProps={{ id: inputId, name, autoComplete }}
            charAfterDialCode="-"
            countrySelectorStyleProps={{ flagStyle: { width: '20px', height: '20px' } }}
            onBlur={() => {
              setSuppressError(false);
              onBlur?.();
            }}
            ref={phoneRef}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            inputMode="email"
            className="body-medium"
            placeholder={emailPlaceholder}
            name={name}
            autoComplete={autoComplete}
            value={value}
            onChange={(e) => {
              setSuppressError(false);
              onChange(e.target.value.trimStart());
            }}
            onBlur={(e) => {
              setSuppressError(false);
              onChange(e.target.value.trim());
              onBlur?.();
            }}
            ref={emailRef}
          />
        )}
      </label>

      {!!shownError && <p className={s.Error}>{shownError}</p>}
    </div>
  );
};
